"use client";

import React from "react";
import { motion } from "framer-motion";
import { CircleCheck as CheckCircle2, TrendingUp, ShieldCheck, PhoneCall, ArrowRight, Target, Award, Users } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const offerings = [{
  title: "Qualified Leads & Appointment Setting",
  description: "High-intent insurance prospects pre-vetted and scheduled directly into your calendar."
}, {
  title: "Telemarketing & Customer Outreach",
  description: "Dedicated outreach specialists engaging prospective policyholders on your behalf."
}, {
  title: "Digital Marketing Support",
  description: "Multi-channel campaigns designed to build trust and capture high-value insurance opportunities."
}, {
  title: "End-to-End Sales Support",
  description: "Full back-office and pipeline support so you can focus strictly on closing deals."
}];
export default function AboutUs() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.215, 0.61, 0.355, 1]
      }
    }
  };
  return <section id="about-us" data-nav="light" className="relative overflow-hidden bg-background py-20 lg:py-28 text-foreground isolate">
      {/* Decorative hairline grid texture */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.05] bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          
          {/* Left Column: Asymmetric Image Composition */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.7,
          ease: "easeOut"
        }} className="relative lg:col-span-5">
            {/* Background Accent Frame */}
            <div className="absolute -inset-3 rounded-2xl bg-gradient-to-tr from-accent/20 via-primary/5 to-transparent blur-xl" aria-hidden="true" />

            <div className="relative rounded-xl border border-border bg-card p-2 shadow-xl">
              <div className="relative overflow-hidden rounded-lg aspect-[4/5]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/rma_team_consultation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/rma_team_consultation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/rma_team_consultation.png-wvc-sizes" alt="RMA Solutions telemarketing and insurance sales support team in action" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                
                {/* Subtle Image Overlay scrim */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent opacity-80" />

                {/* Card overlay badge 1 */}
                <div className="absolute top-4 right-4 rounded-md border border-border/50 bg-background/90 p-3 backdrop-blur-md shadow-md">
                  <div className="flex items-center gap-2.5">
                    <div className="flex size-8 items-center justify-center rounded-full bg-accent/20 text-accent-foreground">
                      <ShieldCheck className="size-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground">Risk-Free Growth</p>
                      <p className="text-[11px] text-muted-foreground">No Upfront Costs</p>
                    </div>
                  </div>
                </div>

                {/* Card overlay stat box 2 */}
                <div className="absolute bottom-4 left-4 right-4 rounded-lg border border-border/40 bg-background/95 p-4 backdrop-blur-md shadow-lg">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
                        <TrendingUp className="size-5" />
                      </div>
                      <div>
                        <div className="text-xs font-medium text-muted-foreground">Partner Performance</div>
                        <div className="text-sm font-bold text-foreground">Ready-to-Submit Clients</div>
                      </div>
                    </div>
                    <Badge variant="secondary" className="border-accent/30 bg-accent/15 text-accent-foreground text-xs font-semibold">
                      100% Performance
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Gold Accent Bar */}
            <div className="absolute -bottom-4 -left-4 -z-10 h-24 w-24 rounded-lg bg-accent/20 blur-2xl" aria-hidden="true" />
          </motion.div>

          {/* Right Column: Narrative & Value Proposition */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="flex flex-col items-start lg:col-span-7">
            {/* Mono Eyebrow Badge */}
            <motion.div variants={itemVariants} className="mb-3">
              <Badge variant="outline" className="gap-1.5 border-accent/40 bg-accent/10 px-3 py-1 font-mono text-xs uppercase tracking-wider text-foreground">
                <span className="size-1.5 rounded-full bg-accent" />
                § About RMA Solutions
              </Badge>
            </motion.div>

            {/* Headline */}
            <motion.h2 variants={itemVariants} className="mb-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Your Strategic Partner for Premier Insurance Growth
            </motion.h2>

            {/* Introductory Paragraphs */}
            <motion.p variants={itemVariants} className="mb-4 text-base text-muted-foreground sm:text-lg leading-relaxed">
              <strong className="font-semibold text-foreground">RMA Solutions LLC</strong> is a professional telemarketing, lead generation, digital marketing, and outsourcing company built to help independent insurance agents and agencies scale efficiently.
            </motion.p>

            <motion.p variants={itemVariants} className="mb-8 text-sm text-muted-foreground sm:text-base leading-relaxed">
              We eliminate acquisition friction by delivering qualified leads, setting live appointments, managing customer outreach, and providing dedicated sales support—all with <span className="font-semibold text-foreground">zero upfront costs</span>. Our performance-based partnership model aligns directly with your success, enabling you to close more deals, maximize commissions, and grow with confidence.
            </motion.p>

            {/* Key Offerings List */}
            <motion.div variants={itemVariants} className="mb-8 grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
              {offerings.map((item, idx) => <div key={idx} data-index={idx} className="group flex gap-3.5 rounded-lg border border-border/80 bg-card p-3.5 shadow-2xs transition-all hover:border-accent/50 hover:shadow-md">
                  <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-accent/20 text-accent-foreground group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                    <CheckCircle2 className="size-4 text-primary group-hover:text-accent-foreground" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{item.title}</h3>
                    <p className="mt-0.5 text-xs text-muted-foreground leading-normal">{item.description}</p>
                  </div>
                </div>)}
            </motion.div>

            {/* Mission Statement Callout Box */}
            <motion.div variants={itemVariants} className="mb-8 w-full rounded-lg border-l-4 border-accent bg-secondary/60 p-4 shadow-2xs">
              <div className="flex items-start gap-3">
                <Target className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Our Core Mission</h4>
                  <p className="mt-1 text-sm font-medium text-foreground italic">
                    "To empower insurance agents with ready-to-submit customers and reliable outsourcing solutions, driving scalable growth without financial risk."
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
                <Link to="/PartnerProgramPage">
                  Explore Partner Program
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent hover:text-accent-foreground">
                <Link to="/GetStartedPage">
                  Get Started Today
                </Link>
              </Button>
            </motion.div>

          </motion.div>

        </div>
      </div>
    </section>;
}